import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { Transition } from "@headlessui/react";
import { TooltipProps } from "./interfaces";

export default function ToolTip(props: TooltipProps) {
  const { children, content, maxWidth, placement = "top", text } = props;
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [hideTimeoutId, setHideTimeoutId] = useState<NodeJS.Timeout | null>(
    null,
  );

  const getOffset = useCallback(
    ({ placement }: { placement: string }): [number, number] => {
      return placement.startsWith("top") ? [0, 6] : [0, 6];
    },
    [],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: placement,
    modifiers: [
      { name: "offset", options: { offset: getOffset } },
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "flip", options: { padding: 8 } },
    ],
  });

  useEffect(() => {
    const handleClick = () => {
      setShowTooltip(false);
      if (timeoutId) clearTimeout(timeoutId);
      if (hideTimeoutId) clearTimeout(hideTimeoutId);
    };

    if (showTooltip) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showTooltip, timeoutId, hideTimeoutId]);

  return (
    <div className="relative">
      <div
        ref={setReferenceElement}
        className="w-fit"
        onMouseEnter={() => {
          if (hideTimeoutId) clearTimeout(hideTimeoutId);
          setTimeoutId(setTimeout(() => setShowTooltip(true), 150));
        }}
        onMouseLeave={() => {
          if (timeoutId) clearTimeout(timeoutId);
          setHideTimeoutId(setTimeout(() => setShowTooltip(false), 150));
        }}
      >
        {children}
      </div>
      {showTooltip &&
        createPortal(
          <Transition
            show={showTooltip}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div
              ref={setPopperElement}
              style={{
                ...styles.popper,
                maxWidth: maxWidth ? `${maxWidth}px` : "280px",
                zIndex: 9999,
              }}
              {...attributes.popper}
              className="w-max rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-lg"
            >
              {text || content}
            </div>
          </Transition>,
          document.body,
        )}
    </div>
  );
}
